import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cx } from '@emotion/css';

import { DeliverySection } from './delivery-section';
import { DeliveryStoreEdit } from './delivery-store-edit';
import { DeliveryStoreNoEdit } from './delivery-store-no-edit';
import { DeliveryChangeButton } from './delivery-change-button/delivery-change-button';
import { IDeliveryProps, IInlineContent } from './delivery.models';
import { useStyles } from './delivery.styles';
import { Icon } from '../../../../../common/icon';
import { IconTypes, IconSizes } from '../../../../../common/icon/icon.models';
import { InfoLabel } from '../../../../../common/info-label';

export const Delivery = ({
  storeData, isEditMode, onStoreChange, isActiveStatus, isNewOrder,
  isFulfilledOrder, purchaseDate, withSeparator = true,
}: IDeliveryProps): JSX.Element => {

  const priceContent: IInlineContent[] = [{
    id: 'order.create.confirm.price.dependency',
    content: <FormattedMessage id="order.create.confirm.price.dependency" />,
  }, {
    id: 'order.create.confirm.price.check',
    content: <FormattedMessage id="order.create.confirm.price.check" />,
  }];

  const classes = useStyles();
  const intl = useIntl();

  return (
    <div
      data-testid="delivery-wrapper"
      className={cx(classes.root, {
        [classes.rootWithBottomMargin]: isActiveStatus,
      })}
    >
      <div
        data-testid="delivery-title-bar"
        className={classes.titleBar}
      >
        <h3
          data-testid="delivery-title"
          className={classes.title}
        >
          <FormattedMessage id="order.create.confirm.delivery.title" />
        </h3>
        {isEditMode && <DeliveryChangeButton onStoreChange={onStoreChange} />}
      </div>
      {isEditMode ? (
        <DeliveryStoreEdit storeData={storeData} withBorder={withSeparator} />
      ) : (
        <DeliveryStoreNoEdit
          storeData={storeData}
          withBorder={false}
          isFulfilledOrder={isFulfilledOrder}
        />
      )}
      {isNewOrder && (
        <InfoLabel
          fullWidth
          className={classes.infoLabel}
          icon={<Icon type={IconTypes.infoMessages} size={IconSizes.sm} />}
          description={(
            <FormattedMessage
              id="order.create.confirm.delivery.info"
              values={{
                strong: ((chunks: ReactNode) => <strong>{chunks}</strong>) as unknown as ReactNode,
              }}
            />
          )}
        />
      )}
      {(isFulfilledOrder && purchaseDate) && (
        <DeliverySection
          name="date"
          title={<FormattedMessage id="order.purchaseDate" />}
        >
          <p
            data-testid="date-message"
            className={classes.paragraphWithMargin}
          >
            {new Date(purchaseDate).toLocaleDateString(intl.locale, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </DeliverySection>
      )}
      {isActiveStatus && isFulfilledOrder && (
        <DeliverySection
          name="price"
          title={<FormattedMessage id="order.create.confirm.price.title" />}
          inlineContent={priceContent}
        >
          <p
            data-testid="price-link-wrapper" // Don't remove data-testid, it is used by Yandex Metrica
            className={classes.paragraphWithMargin}
          >
            <a
              href={`tel:${storeData?.phonenumber}`}
              data-testid="price-link" // Don't remove data-testid, it is used by Yandex Metrica
              className={classes.linkNoUnderline}
            >
              <FormattedMessage id="order.create.confirm.price.button" />
            </a>
          </p>
        </DeliverySection>
      )}
    </div>
  );
};
